/**
 * ToolCard — barrel of small cards rendered INSIDE a chat MessageBubble.
 *
 * Each card represents one "tool result" the AI returned (a trip plan,
 * a ground-transport leg, a smart alternative, an insider-tips list, a
 * hotel, or a flight). They all share the same look-and-feel language
 * (rounded-xl + small uppercase headers + lucide icons) so the chat
 * feels like a single concierge folder.
 *
 * Extracted out of AIChat.js (1000+ lines) — keeps the orchestrator
 * focused on state + sendQuery + persistence; rendering lives here.
 */
import React from 'react';
import {
  MapPin, Clock, Sun, Gem, Train, Sparkles, Lightbulb, Plane, Star, Wifi, ArrowRight,
  Flame, Zap, Award, ShieldCheck, Coffee,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatDateRange, getBadgeColor } from './helpers';
import PriceTag from '../pricing/PriceTag';
import { FlightPriceTotal, HotelOccupancyLine, PriceRefreshBadge } from './TravelerSummary';
import ServedByLine from './ServedByLine';
import CardMetaLine, { buildHotelMeta, buildFlightMeta } from './CardMetaLine';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const readableStopCount = (value, language) => {
  if (!String(language || '').startsWith('bg')) return value;
  const source = String(value || '');
  if (/^(direct|non.stop|0 stops?)$/i.test(source)) return 'Директен';
  const number = source.match(/^(\d+)\s+stops?$/i);
  if (number) return number[1] === '1' ? '1 прекачване' : `${number[1]} прекачвания`;
  if (/connections unverified|check connections/i.test(source)) return 'Прекачванията се проверяват';
  return value;
};


/** Extract the first integer from a price string ("€189", "€100-€200" → 189).
 *  Used to feed the displayedPrice into /api/pricing/refresh. */
const extractFirstNum = (price) => {
  if (!price) return null;
  const m = String(price).match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
};

/** Origin guess from a "Sofia (SOF)" label. Falls back to "SOF" so the
 *  refresh CTA always works for our default departure. */
const extractIata = (label) => {
  if (!label) return '';
  const m = String(label).match(/\(([A-Z]{3})\)/);
  return m ? m[1] : '';
};

/**
 * Phase-2 reason chips — computed at list level so each card knows why
 * it's worth clicking. Zero backend changes: everything is derived from
 * the payload we already ship. Empty array when we can't verify a claim.
 *
 * Rules (flights):
 *   • Cheapest       — min priceNumeric in the current list (>=2 flights).
 *   • Fastest        — min durationMinutes.
 *   • Direct         — stops === 0 or stops label starts with "0" / "direct".
 *   • Fewest stops   — item has fewer stops than every other item.
 *
 * Rules (hotels):
 *   • Highest rating       — max rating in list (>=2 hotels).
 *   • Best value           — top of the list AND rating >= 8.5 (approx heuristic).
 *   • City centre          — centrality flag OR "center"/"downtown" in location.
 *   • Free cancellation    — hotel.freeCancellation === true.
 *   • Breakfast included   — hotel.breakfastIncluded === true.
 */
export function computeFlightChips(flights) {
  if (!Array.isArray(flights) || flights.length === 0) return {};
  const prices = flights.map((f) => Number(f.priceNumeric) || Infinity);
  const durs = flights.map((f) => {
    if (typeof f.durationMinutes === 'number') return f.durationMinutes;
    const m = String(f.duration || '').match(/(\d+)\s*h\s*(\d+)?/i);
    return m ? (parseInt(m[1], 10) * 60 + (parseInt(m[2] || '0', 10))) : Infinity;
  });
  const stopsList = flights.map((f) => {
    if (typeof f.numStops === 'number') return f.numStops;
    const s = String(f.stops || '').toLowerCase();
    if (s.startsWith('0') || s.includes('direct') || s.includes('non-stop')) return 0;
    const m = s.match(/^(\d+)/);
    return m ? parseInt(m[1], 10) : 99;
  });
  const minPrice = Math.min(...prices);
  const minDur = Math.min(...durs);
  const minStops = Math.min(...stopsList);
  const map = {};
  flights.forEach((f, i) => {
    const key = f.id || `${f.departure}-${f.arrival}-${f.priceNumeric || i}`;
    const chips = [];
    if (flights.length >= 2 && prices[i] === minPrice && prices[i] !== Infinity) chips.push('cheapest');
    if (stopsList[i] === 0) chips.push('direct');
    if (flights.length >= 2 && durs[i] === minDur && durs[i] !== Infinity && !chips.includes('direct')) chips.push('fastest');
    if (flights.length >= 3 && stopsList[i] === minStops && stopsList[i] > 0 && !chips.includes('direct')) chips.push('fewestStops');
    map[key] = chips.slice(0, 3);
  });
  return map;
}

export function computeHotelChips(hotels) {
  if (!Array.isArray(hotels) || hotels.length === 0) return {};
  const ratings = hotels.map((h) => Number(h.rating) || 0);
  const maxRating = Math.max(...ratings);
  const map = {};
  hotels.forEach((h, i) => {
    const key = h.id || `${h.name}-${h.price}-${i}`;
    const chips = [];
    if (hotels.length >= 2 && ratings[i] === maxRating && maxRating > 0) chips.push('topRated');
    const loc = String(h.location || '').toLowerCase();
    if (h.centralityScore >= 0.7 || loc.includes('center') || loc.includes('centre') || loc.includes('downtown') || loc.includes('old town')) {
      chips.push('centrallyLocated');
    }
    if (h.freeCancellation === true) chips.push('freeCancellation');
    if (h.breakfastIncluded === true) chips.push('breakfast');
    if (hotels.length >= 3 && i === 0 && ratings[i] >= 8.5 && !chips.includes('topRated')) chips.push('bestValue');
    map[key] = chips.slice(0, 4);
  });
  return map;
}

const CHIP_STYLE = {
  cheapest:          { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Flame },
  fastest:           { color: 'bg-orange-50 text-orange-700 border-orange-200',   icon: Zap },
  direct:            { color: 'bg-blue-50 text-blue-700 border-blue-200',         icon: Plane },
  fewestStops:       { color: 'bg-indigo-50 text-indigo-700 border-indigo-200',   icon: Sparkles },
  topRated:          { color: 'bg-amber-50 text-amber-800 border-amber-200',      icon: Award },
  bestValue:         { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Award },
  centrallyLocated:  { color: 'bg-blue-50 text-blue-700 border-blue-200',       icon: MapPin },
  freeCancellation:  { color: 'bg-sky-50 text-sky-700 border-sky-200',            icon: ShieldCheck },
  breakfast:         { color: 'bg-rose-50 text-rose-700 border-rose-200',         icon: Coffee },
};

/**
 * ReasonChipRow — inline chip strip rendered above/below a card body.
 * Reads localised labels from `chat.chip.*` translation keys.
 */
function ReasonChipRow({ chips, testId }) {
  const { t } = useLanguage();
  if (!chips || chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {chips.map((key) => {
        const cfg = CHIP_STYLE[key];
        if (!cfg) return null;
        const Icon = cfg.icon;
        return (
          <span
            key={key}
            data-testid={`${testId}-${key}`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.color}`}
          >
            <Icon className="w-2.5 h-2.5" />
            {t(`chat.chip.${key}`)}
          </span>
        );
      })}
    </div>
  );
}

// ============================================================
// 1. TripPlanCard — the in-bubble blue/cyan trip-plan box
// ============================================================
export function TripPlanCard({ tripPlan }) {
  const { t } = useLanguage();
  if (!tripPlan) return null;
  return (
    <div className="mt-3 p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-5 h-5 text-blue-600" />
        <h4 className="font-bold text-blue-900">
          {Array.isArray(tripPlan.destinations) && tripPlan.destinations.length > 1
            ? tripPlan.destinations.map((d) => typeof d === 'string' ? d : (d.city || d.name || d.destination || '')).filter(Boolean).join(' → ')
            : tripPlan.destination}
        </h4>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2 text-gray-700">
          <Clock className="w-4 h-4 text-blue-500" />
          <span>{tripPlan.dates}</span>
        </div>
        <div className="flex items-center gap-2 text-gray-700">
          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getBadgeColor(tripPlan.budget_type)}`}>
            {tripPlan.budget_type}
          </span>
        </div>
      </div>
      {(tripPlan.bestTime || tripPlan.localHighlight) && (
        <div className="mt-3 pt-3 border-t border-blue-200/60 space-y-2">
          {tripPlan.bestTime && (
            <div className="flex items-start gap-2 text-xs text-gray-700">
              <Sun className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-gray-800">{t('chat.bestTime')}: </span>
                {tripPlan.bestTime}
              </div>
            </div>
          )}
          {tripPlan.localHighlight && (
            <div className="flex items-start gap-2 text-xs text-gray-700">
              <Gem className="w-4 h-4 text-brand-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-gray-800">{t('chat.localGem')}: </span>
                {tripPlan.localHighlight}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// 2. TransportList — info-only ground transport segments
// ============================================================
export function TransportList({ transports, messageId }) {
  const { t } = useLanguage();
  if (!transports?.length) return null;
  return (
    <div className="mt-3 space-y-2" data-testid={`chat-transports-${messageId}`}>
      <h5 className="text-sm font-bold text-gray-800 flex items-center gap-2 px-1">
        <Train className="w-4 h-4 text-emerald-600" />
        {t('chat.transports')}
      </h5>
      {transports.map((seg, idx) => (
        <div
          key={`${messageId}-transport-${idx}`}
          data-testid={`chat-transport-${idx}`}
          className="bg-white rounded-xl border border-emerald-200/60 p-3 flex items-center gap-3 shadow-sm"
        >
          <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
            <Train className="w-4 h-4 text-emerald-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm text-gray-900 truncate">
              {seg.from} → {seg.to}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {seg.mode || 'train'}{seg.duration ? ` · ${seg.duration}` : ''}{seg.estimate ? ` · ${seg.estimate}` : ''}
            </div>
            {seg.note && <div className="text-[11px] text-gray-400 mt-0.5 truncate">{seg.note}</div>}
          </div>
          <a
            href={`https://www.google.com/travel/explore?q=${encodeURIComponent(`${seg.from} to ${seg.to} ${seg.mode || 'train'}`)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`chat-transport-cta-${idx}`}
            className="text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 transition flex-shrink-0"
          >
            {t('chat.transportCta')}
          </a>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// 3. AlternativeCard — single "smart variation" callout
// ============================================================
export function AlternativeCard({ alternative, messageId }) {
  const { t } = useLanguage();
  if (!alternative) return null;
  return (
    <div
      className="mt-3 p-3 bg-brand-amber-50 rounded-xl border border-brand-amber-200"
      data-testid={`chat-alternative-${messageId}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-indigo-600" />
        <span className="text-xs font-bold uppercase tracking-wide text-indigo-700">
          {t('chat.alternativeLabel')}
          {alternative.label ? ` · ${alternative.label}` : ''}
        </span>
      </div>
      <p className="text-sm text-gray-800 leading-snug">{alternative.summary}</p>
    </div>
  );
}

// ============================================================
// 4. InsiderTipsCard — amber "concierge tips" list
// ============================================================
export function InsiderTipsCard({ tips, messageId }) {
  const { t } = useLanguage();
  if (!tips?.length) return null;
  return (
    <div className="mt-3 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200/70 shadow-sm">
      <div className="flex items-center gap-2 mb-2">
        <Lightbulb className="w-4 h-4 text-amber-600" />
        <h5 className="text-sm font-bold text-amber-900">{t('chat.insiderTips')}</h5>
      </div>
      <ul className="space-y-1.5">
        {tips.map((tip, idx) => (
          <li
            key={`${messageId}-tip-${idx}`}
            className="text-xs text-gray-800 leading-relaxed flex gap-2"
          >
            <span className="text-amber-600 font-bold flex-shrink-0">·</span>
            <span>{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================
// 5. HotelCard — single hotel row with image + amenities + Book
// ============================================================
export function HotelCard({ hotel, searchContext, language, onBook, reasonChips = null }) {
  const { t } = useLanguage();
  if (!hotel) return null;
  const checkIn = hotel.checkIn || searchContext?.checkIn;
  const checkOut = hotel.checkOut || searchContext?.checkOut;
  const id = hotel.id || hotel.name;
  const handleBook = () => {
    // Forward reason chips into the booking payload so `affiliate_redirect`
    // includes them in the funnel (lets us measure CTR per chip type).
    const bookingHotel = reasonChips?.length && !hotel.reasonChips
      ? { ...hotel, reasonChips }
      : hotel;
    onBook(bookingHotel);
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200">
      <div className="flex flex-col sm:flex-row">
        {hotel.image && hotel.nameAiGenerated !== true ? (
          <img
            src={hotel.image}
            alt={hotel.name}
            className="w-full h-36 sm:w-28 sm:h-28 object-cover flex-shrink-0"
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-36 sm:w-28 sm:h-28 bg-blue-50 flex-shrink-0 flex items-center justify-center">
            <MapPin className="w-8 h-8 text-blue-300" aria-label="Hotel photo unavailable" />
          </div>
        )}
        <div className="flex-1 p-3 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h6 className="font-bold text-gray-900 text-sm leading-snug break-words">{hotel.name}</h6>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border flex-shrink-0 ${getBadgeColor(hotel.type)}`}>
              {language === 'bg' && hotel.type === 'standard' ? 'Настаняване' : hotel.type}
            </span>
          </div>
          {/* iter95 · B3 — every AI-authored hotel must be disclosed as an
              estimate. Real Booking.com inventory takes over on click. */}
          {hotel.estimated && (
            <div
              data-testid={`chat-hotel-estimate-chip-${id}`}
              className="mb-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200 text-[10px] font-medium tracking-wide"
              title={t('hotel.estimateTooltip') || 'Показан е AI пример. Реалната наличност и цена се зареждат при клик.'}
            >
              <span aria-hidden>ⓘ</span>
              <span>{t('hotel.estimate') || 'AI пример · реални цени при клик'}</span>
            </div>
          )}
          {reasonChips && reasonChips.length > 0 && (
            <div className="mb-2">
              <ReasonChipRow chips={reasonChips} testId={`chat-hotel-chips-${id}`} />
            </div>
          )}
          <CardMetaLine
            segments={buildHotelMeta(hotel, reasonChips, language, t)}
            testId={`chat-hotel-meta-${id}`}
          />
          <div className="space-y-1 text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{hotel.location}</span>
              {hotel.nearby && hotel.nearbyOf && (
                <span
                  data-testid={`chat-hotel-nearby-chip-${id}`}
                  className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-medium tracking-wide whitespace-nowrap"
                  title={hotel.nearbyDistanceKm ? `${hotel.nearbyDistanceKm} km от ${hotel.nearbyOf}` : hotel.nearbyOf}
                >
                  {t('hotel.nearby') || 'Наблизо'} · {hotel.nearbyOf}
                  {hotel.nearbyDistanceKm ? ` · ${Math.round(hotel.nearbyDistanceKm)} км` : ''}
                </span>
              )}
            </div>
            {Number.isFinite(Number(hotel.rating))
              && Number(hotel.rating) > 0
              && [5, 10].includes(Number(hotel.ratingScale))
              && Number(hotel.rating) <= Number(hotel.ratingScale)
              && !hotel.estimated && hotel.inventorySource !== 'ai_estimate' && (
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                <span>{hotel.rating}/{hotel.ratingScale}</span>
              </div>
            )}
            <HotelOccupancyLine occupancy={hotel.occupancy} rooms={hotel.rooms} />
            {checkIn && checkOut && (
              <div className="flex items-center gap-1 text-blue-700 font-medium" data-testid={`chat-hotel-dates-${id}`}>
                <Clock className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{formatDateRange(checkIn, checkOut, language)}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Wifi className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{hotel.amenities}</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
            {hotel.inventorySource === 'redirect_only' ? (
              <span className="text-sm text-blue-700 font-medium" data-testid={`chat-hotel-price-${id}`}>
                {language === 'bg' ? 'Цените се проверяват' : 'Check current rates'}
              </span>
            ) : (
              <PriceTag
                price={hotel.price}
                tier={hotel.priceTier || 'estimated'}
                size="base"
                testId={`chat-hotel-price-${id}`}
              />
            )}
            <button
              data-testid={`chat-book-hotel-${id}`}
              onClick={handleBook}
              className="w-full sm:w-auto px-4 py-2 sm:py-1.5 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 active:bg-blue-700 transition-colors font-semibold"
            >
              {hotel.inventorySource === 'redirect_only' ? (language === 'bg' ? 'Провери наличности' : 'Check availability') : t('chat.book')}
            </button>
          </div>
          {hotel.inventorySource !== 'redirect_only' && (hotel.priceVolatile || hotel.priceVolatileDirection) && (
            <PriceRefreshBadge
              destination={extractIata(hotel.iata) || extractIata(hotel.location)}
              origin="SOF"
              displayedPrice={extractFirstNum(hotel.price)}
              direction={hotel.priceVolatileDirection}
              apiBase={API_BASE}
            />
          )}
          <p className="mt-2 text-[10px] text-gray-400 leading-snug" data-testid={`chat-hotel-disclaimer-${id}`}>
            {hotel.priceDisclaimer || t('chat.priceDisclaimer')}
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 6. FlightCard — single flight row with route + stops + Book
// ============================================================
export function FlightCard({ flight, language, onBook, reasonChips = null }) {
  const { t } = useLanguage();
  if (!flight) return null;
  const id = flight.id || `${flight.departure}-${flight.arrival}`;
  const handleBook = () => {
    if (reasonChips && reasonChips.length > 0 && flight && !flight.reasonChips) {
      flight.reasonChips = reasonChips;
    }
    onBook(flight);
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between mb-2">
        <div>
          {Number.isInteger(flight.legIndex) && flight.legCount > 1 && (
            <span className="inline-block text-[10px] uppercase tracking-wide text-blue-700 font-bold mb-1">
              {language === 'bg' ? 'Полет' : 'Flight'} {flight.legIndex + 1}/{flight.legCount}
            </span>
          )}
          <h6 className="font-bold text-gray-900 text-sm">{flight.airline}</h6>
          <div className="flex items-center gap-2 text-xs text-gray-600 mt-1">
            <span className="font-medium">{flight.departure}</span>
            <ArrowRight className="w-3 h-3" />
            <span className="font-medium">{flight.arrival}</span>
          </div>
          {(flight.departureDate || flight.departure_at) && (
            <div className="flex items-center gap-1 text-xs text-blue-700 font-medium mt-1" data-testid={`chat-flight-dates-${id}`}>
              <Clock className="w-3 h-3 flex-shrink-0" />
              <span>{formatDateRange(flight.departureDate || flight.departure_at, flight.returnDate || flight.return_at, language)}</span>
            </div>
          )}
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getBadgeColor(flight.type)}`}>
          {language === 'bg' && flight.type === 'real' ? 'От доставчик' : language === 'bg' && flight.inventorySource === 'redirect_only' ? 'Търсене' : flight.type}
        </span>
      </div>
      {reasonChips && reasonChips.length > 0 && (
        <div className="mb-2">
          <ReasonChipRow chips={reasonChips} testId={`chat-flight-chips-${id}`} />
        </div>
      )}
      <CardMetaLine
        segments={buildFlightMeta(flight, reasonChips, language, t)}
        testId={`chat-flight-meta-${id}`}
      />
      <div className="flex items-center justify-between text-xs text-gray-600 flex-wrap gap-y-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{flight.duration}</span>
          </div>
          <span className="text-gray-400">•</span>
          <span>{readableStopCount(flight.stops, language)}</span>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <div className="flex items-center gap-2">
            {flight.inventorySource === 'redirect_only' ? (
              <span className="text-sm text-blue-700 font-medium" data-testid={`chat-flight-price-${id}`}>
                {language === 'bg' ? 'Цените се проверяват' : 'Check current fares'}
              </span>
            ) : (
              <PriceTag
                price={flight.price}
                tier={flight.priceTier || 'estimated'}
                size="sm"
                suffix={flight.travelers > 1 ? (language === 'bg' ? '/човек' : '/pax') : ''}
                testId={`chat-flight-price-${id}`}
              />
            )}
            <FlightPriceTotal
              priceTotal={flight.priceTotal}
              travelers={flight.travelers}
            />
          </div>
          <button
            data-testid={`chat-book-flight-${id}`}
            onClick={handleBook}
            className="px-4 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 active:bg-blue-700 transition-colors font-semibold text-sm"
          >
            {flight.inventorySource === 'redirect_only' ? (language === 'bg' ? 'Провери полети' : 'Check flights') : t('chat.book')}
          </button>
        </div>
      </div>
      {flight.inventorySource === 'redirect_only' && (
        <p className="w-full mt-2 text-[11px] text-amber-700">
          {language === 'bg' ? 'Търсене без потвърдена наличност или цена.' : 'Search only — no confirmed price or availability.'}
        </p>
      )}
      {flight.inventorySource !== 'redirect_only' && (flight.priceVolatile || flight.priceVolatileDirection) && (
        <PriceRefreshBadge
          destination={extractIata(flight.arrival)}
          origin={extractIata(flight.departure) || 'SOF'}
          displayedPrice={extractFirstNum(flight.price)}
          direction={flight.priceVolatileDirection}
          apiBase={API_BASE}
        />
      )}
      <ServedByLine iata={extractIata(flight.arrival)} testId={`chat-flight-servedby-${id}`} />
    </div>
  );
}

// ============================================================
// 7. HotelList / FlightList — section wrappers used inside MessageBubble
// ============================================================
export function HotelList({ hotels, searchContext, language, onBookHotel }) {
  const chipMap = React.useMemo(() => computeHotelChips(hotels), [hotels]);
  if (!hotels?.length) return null;
  return (
    <div className="mt-3 space-y-2">
      <h5 className="text-sm font-bold text-gray-800 flex items-center gap-2 px-1">
        <MapPin className="w-4 h-4 text-blue-600" />
        {language === 'bg' ? 'Предложения за настаняване' : 'Hotel options'}
      </h5>
      {hotels.map((hotel, i) => {
        const key = hotel.id || `${hotel.name}-${hotel.price}-${i}`;
        return (
          <HotelCard
            key={hotel.id || `hotel-${hotel.name}-${hotel.price}`}
            hotel={hotel}
            searchContext={searchContext}
            language={language}
            onBook={onBookHotel}
            reasonChips={chipMap[key] || []}
          />
        );
      })}
    </div>
  );
}

export function FlightList({ flights, language, onBookFlight }) {
  const chipMap = React.useMemo(() => computeFlightChips(flights), [flights]);
  if (!flights?.length) return null;
  return (
    <div className="mt-3 space-y-2">
      <h5 className="text-sm font-bold text-gray-800 flex items-center gap-2 px-1">
        <Plane className="w-4 h-4 text-blue-600" />
        {language === 'bg' ? 'Полети по маршрута' : 'Flight options'}
      </h5>
      {flights.map((flight, i) => {
        const key = flight.id || `${flight.departure}-${flight.arrival}-${flight.priceNumeric || i}`;
        return (
          <FlightCard
            key={flight.id || `flight-${flight.departure}-${flight.arrival}-${flight.price}`}
            flight={flight}
            language={language}
            onBook={onBookFlight}
            reasonChips={chipMap[key] || []}
          />
        );
      })}
    </div>
  );
}
